import clsx from "clsx"
import { useId } from "react"
import { NpErrorMessage } from "src/components/np-design/components/NpErrorMessage"
import { NpIcon } from "src/components/np-design/components/NpIcon"
import { NpInputLabel } from "src/components/np-design/components/input/NpInputLabel"
import { BusyIndicator } from "src/components/unstyled/BusyIndicator"
import { useErrorMessageId } from "src/utilities/errorScrolling"

export type NpSelectOption = {
  value: string
  label: string
}

export type NpSelectProps = {
  placeholder: string
  autoComplete: string
  required?: boolean
  disabled?: boolean
  options: NpSelectOption[]
  value: string | undefined
  onChange: (value: string) => void
  loading?: boolean
  error?: string
  showError?: boolean
}

export const NpSelect = ({
  placeholder,
  autoComplete,
  required,
  disabled,
  options,
  value,
  onChange,
  loading,
  error,
  showError,
}: NpSelectProps) => {
  const id = useId()
  const errorId = useErrorMessageId()

  const label = placeholder + (required ? " *" : "")
  const hasError = !!error && showError
  const inert = disabled || loading

  return (
    <div
      id={error && showError ? errorId : undefined}
      inert={inert}
      className="group text-default relative flex flex-col gap-1 text-base"
    >
      <NpInputLabel htmlFor={id} elevated={!!value} hasError={hasError}>
        {label}
      </NpInputLabel>

      <select
        id={id}
        name={autoComplete}
        autoComplete={autoComplete}
        aria-placeholder={placeholder}
        required={required}
        disabled={inert}
        value={value || ""}
        onChange={(event) => onChange(event.currentTarget.value)}
        onPaste={(event) => onChange(event.currentTarget.value)}
        className={clsx("h-12 rounded-xl border py-3 pr-9 pl-4 placeholder-transparent", {
          "border-np-green25": !hasError && !inert,
          "border-np-gray25": !hasError && inert,
          "hover:border-np-green focus:border-np-green": !hasError && !inert,
          "border-np-error!": hasError,
          "text-transparent": !value || loading,
          "cursor-pointer": !inert,
          "cursor-not-allowed": inert,
        })}
      >
        {(placeholder || required) && (
          <option value="" className="text-muted">
            {label}
          </option>
        )}
        {options.map(({ value, label }) => (
          <option key={value} value={value} className="text-default">
            {label}
          </option>
        ))}
      </select>

      {loading && (
        <div className="text-default pointer-events-none absolute inset-0 flex items-center justify-center">
          <BusyIndicator />
        </div>
      )}

      {!loading && (
        <NpIcon
          name="keyboard_arrow_down"
          className="text-default pointer-events-none absolute top-1/2 right-2 flex -translate-y-1/2 items-center"
        />
      )}

      {hasError && (
        <div className="px-4">
          <NpErrorMessage message={error} />
        </div>
      )}
    </div>
  )
}
